import React, { useState, useEffect } from 'react';
import { Clock, CheckCircle2, AlertCircle, Loader2, MapPin, Calendar, LogIn, LogOut } from 'lucide-react';
import { attendanceService } from '../services/attendanceService';
import { useAuth } from '../context/AuthContext';

// Shared check-in / check-out widget — location-based only (no face
// recognition, no Wi-Fi/IP checking). GPS location is mandatory and is
// verified server-side against the admin-configured office location.
// Used on EmployeeDashboard, SalesRepDashboard, SalesManagerDashboard and
// FinanceDashboard so every role except SuperAdmin/HR can mark their own
// daily attendance.
const FaceAttendanceWidget = () => {
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [locating, setLocating] = useState(false);
  const [result, setResult] = useState(null);

  const [todayRecord, setTodayRecord] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [initLoading, setInitLoading] = useState(true);

  // Feature: force location permission before allowing check-in/check-out.
  // 'checking' | 'granted' | 'denied' | 'prompt' | 'unsupported'
  const [locationStatus, setLocationStatus] = useState('checking');
  const [requestingLocation, setRequestingLocation] = useState(false);

  const loadDashboardData = async () => {
    setInitLoading(true);
    try {
      const employeeId = user?.employee_id || user?.user_id;

      const attRes = await attendanceService.getEmployeeHistory(
        employeeId,
        new Date().getMonth() + 1,
        new Date().getFullYear()
      );
      const todayStr = new Date().toISOString().split('T')[0];
      const today = attRes.data?.find(r => r.date === todayStr);
      setTodayRecord(today || null);

      if (user?.department) {
        const schRes = await attendanceService.getSchedules(user.department);
        if (schRes.data && schRes.data.length > 0) {
          setSchedule(schRes.data[0]);
        }
      }
    } catch (err) {
      console.error("Attendance widget wiring error:", err);
    } finally {
      setInitLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Feature: watch the browser's actual geolocation permission state, live —
  // updates automatically if the user allows/blocks it from the browser UI.
  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationStatus('unsupported');
      return;
    }
    let permissionStatus;
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions.query({ name: 'geolocation' })
        .then((status) => {
          permissionStatus = status;
          setLocationStatus(status.state);
          status.onchange = () => setLocationStatus(status.state);
        })
        .catch(() => setLocationStatus('prompt'));
    } else {
      // Permissions API not available (e.g. some Safari versions) — fall back to asking directly.
      setLocationStatus('prompt');
    }
    return () => { if (permissionStatus) permissionStatus.onchange = null; };
  }, []);

  const requestLocationPermission = () => {
    setRequestingLocation(true);
    navigator.geolocation.getCurrentPosition(
      () => { setLocationStatus('granted'); setRequestingLocation(false); },
      () => { setLocationStatus('denied'); setRequestingLocation(false); },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  const getLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by this browser.'));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => reject(new Error('Location access is required to mark attendance. Please enable GPS/location permission and try again.')),
        { enableHighAccuracy: true, timeout: 15000 }
      );
    });
  };

  const handleMark = async (mode) => {
    if (locationStatus !== 'granted') return;
    setResult(null);
    setLocating(true);
    let coords;
    try {
      coords = await getLocation();
    } catch (err) {
      setLocating(false);
      setResult({ type: 'error', message: err.message });
      return;
    }
    setLocating(false);

    setLoading(true);
    try {
      await attendanceService.markAttendance(mode, coords.lat, coords.lng);
      setResult({ type: 'success', message: (mode === 'checkin' ? 'Check-In' : 'Check-Out') + ' Successful!' });
      loadDashboardData();
    } catch (err) {
      setResult({ type: 'error', message: err.message || 'Failed to mark attendance.' });
    } finally {
      setLoading(false);
    }
  };

  const getStatusText = () => {
    if (!todayRecord) return "Not Marked Yet";
    if (todayRecord.check_in && !todayRecord.check_out) return "In Office (Logged In)";
    if (todayRecord.check_in && todayRecord.check_out) return "Shift Completed";
    return todayRecord.status;
  };

  const canCheckIn = !todayRecord || !todayRecord.check_in;
  const canCheckOut = Boolean(todayRecord && todayRecord.check_in && !todayRecord.check_out);
  const locationBlocked = locationStatus !== 'granted';
  const busy = loading || locating;

  return (
    <div className="space-y-6">
      {/* Feature: forced location permission popup — blocks check-in/out until allowed */}
      {(locationStatus === 'prompt' || locationStatus === 'denied' || locationStatus === 'unsupported') && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-card shadow-elevated w-full max-w-sm p-6 text-center">
            <div className="w-16 h-16 bg-primary/10 text-primary rounded-full flex items-center justify-center mx-auto mb-4">
              <MapPin className="w-8 h-8" />
            </div>
            <h2 className="text-lg font-black text-gray-900 mb-2">Location Access Required</h2>
            <p className="text-sm text-gray-500 mb-6">
              {locationStatus === 'denied'
                ? "Location access is blocked for this site. Please enable it from your browser's site settings, then click Allow again."
                : locationStatus === 'unsupported'
                ? 'Your browser does not support location access, which is required to mark attendance. Please use a different browser.'
                : 'To check in or check out, please allow this site to access your location.'}
            </p>
            {locationStatus !== 'unsupported' && (
              <button
                onClick={requestLocationPermission}
                disabled={requestingLocation}
                className="w-full bg-primary text-white py-3 rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {requestingLocation ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
                Allow Location
              </button>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Main Attendance Module */}
        <div className="lg:col-span-2 bg-white rounded-card border border-gray-100 shadow-soft overflow-hidden">
          <div className="p-4 border-b border-gray-50 flex items-center justify-between bg-gray-50/50">
            <h2 className="text-[10px] font-black text-primary uppercase tracking-[0.2em] flex items-center gap-2">
              <MapPin className="w-4 h-4" /> Location-Based Attendance
            </h2>
            {locating && (
              <span className="text-[10px] font-bold text-amber-600 bg-amber-50 px-3 py-1 rounded-full flex items-center gap-1 animate-pulse border border-amber-100">
                <MapPin className="w-3 h-3" /> CHECKING LOCATION
              </span>
            )}
            {locationBlocked && locationStatus !== 'checking' && (
              <span className="text-[10px] font-bold text-red-600 bg-red-50 px-3 py-1 rounded-full flex items-center gap-1 border border-red-100">
                <MapPin className="w-3 h-3" /> LOCATION NOT ALLOWED
              </span>
            )}
          </div>

          <div className="p-10 flex flex-col items-center justify-center">
            <div className="text-center flex flex-col items-center space-y-6 w-full">
              <div className="w-32 h-32 bg-gray-50 rounded-full flex items-center justify-center text-gray-200 border-2 border-dashed border-gray-100 mx-auto">
                <Clock className="w-12 h-12" />
              </div>
              <p className="text-sm text-gray-500 max-w-xs mx-auto font-medium leading-relaxed">
                Mark your attendance below. You must be within the office location to check in or out.
              </p>

              <div className="flex justify-center gap-4 w-full max-w-sm">
                <button
                  onClick={() => handleMark('checkin')}
                  disabled={busy || !canCheckIn || locationBlocked}
                  className="flex-1 flex items-center justify-center gap-2 bg-green-600 text-white py-3.5 rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg shadow-green-100 hover:scale-105 transition-all disabled:opacity-30 disabled:hover:scale-100"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
                  Check-In
                </button>
                <button
                  onClick={() => handleMark('checkout')}
                  disabled={busy || !canCheckOut || locationBlocked}
                  className="flex-1 flex items-center justify-center gap-2 bg-amber-600 text-white py-3.5 rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg shadow-amber-100 hover:scale-105 transition-all disabled:opacity-30 disabled:hover:scale-100"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogOut className="w-4 h-4" />}
                  Check-Out
                </button>
              </div>
            </div>

            {result && (
              <div className={'mt-8 w-full max-w-md p-5 rounded-2xl border-2 flex items-center gap-4 ' + (result.type === 'success' ? 'bg-green-50 border-green-100 text-green-700' : 'bg-red-50 border-red-100 text-red-700')}>
                {result.type === 'success' ? <CheckCircle2 className="w-6 h-6" /> : <AlertCircle className="w-6 h-6" />}
                <p className="text-sm font-bold">{result.message}</p>
              </div>
            )}
          </div>
        </div>

        {/* Real-time Status Sidebar */}
        <div className="space-y-4">
           <div className="bg-white p-6 rounded-card border border-gray-100 shadow-soft space-y-5">
              <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] border-b pb-3 flex items-center gap-2">
                 <Calendar className="w-4 h-4" /> Shift Timing
              </h3>
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-50 text-blue-600 rounded-2xl"><Clock className="w-6 h-6" /></div>
                <div>
                  <p className="text-[10px] text-gray-400 font-black uppercase tracking-tighter">Office Hours</p>
                  <p className="text-base font-black text-gray-900 leading-none mt-1">
                    {schedule ? (schedule.start_time + ' - ' + schedule.end_time) : '09:00 - 18:00'}
                  </p>
                </div>
              </div>
           </div>

           <div className="bg-white p-6 rounded-card border border-gray-100 shadow-soft space-y-5">
              <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] border-b pb-3 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" /> Attendance Status
              </h3>
              <div className="flex items-center gap-4">
                <div className={'p-3 rounded-2xl ' + (todayRecord ? 'bg-green-50 text-green-600' : 'bg-gray-50 text-gray-400')}>
                   {todayRecord?.check_out ? <CheckCircle2 className="w-6 h-6" /> : <Clock className="w-6 h-6" />}
                </div>
                <div>
                  <p className="text-[10px] text-gray-400 font-black uppercase tracking-tighter">Status Today</p>
                  <p className="text-base font-black text-gray-900 mt-1 uppercase tracking-tighter">
                    {initLoading ? 'Loading...' : getStatusText()}
                  </p>
                </div>
              </div>
              {todayRecord?.check_in && (
                <div className="bg-gray-50 p-3 rounded-xl border border-gray-100">
                   <p className="text-[10px] font-bold text-gray-400">IN: {todayRecord.check_in}</p>
                   {todayRecord.check_out && <p className="text-[10px] font-bold text-gray-400">OUT: {todayRecord.check_out}</p>}
                </div>
              )}
           </div>
        </div>

      </div>
    </div>
  );
};

export default FaceAttendanceWidget;